import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { PROGRESS_ACCENT_BG_CLASSES, type ProgressAccent } from './progressAccents';

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'aria-valuenow' | 'aria-valuemin' | 'aria-valuemax'> {
    /**
     * 0..1. Out-of-range values are clamped, and a non-finite value (a `0 / 0`
     * ratio reaches this as NaN) is treated as 0, before rendering the fill
     * and the aria attributes.
     */
    value: number;
    accent?: ProgressAccent;
    label?: string;
    showValue?: boolean;
}

const TRACK_CLASSES = 'h-1.5 rounded-full bg-space-950 overflow-hidden';
const FILL_CLASSES = 'h-full transition-[width] duration-300';

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
    { value, accent = 'zone', label, showValue = false, className, ...rest },
    ref,
) {
    const clamped = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
    const percent = Math.round(clamped * 100);

    return (
        <div ref={ref} className={cn('flex flex-col gap-1', className)} {...rest}>
            {(label || showValue) && (
                <div className="flex items-center justify-between text-micro text-content-muted">
                    {label && <span>{label}</span>}
                    {showValue && <span>{percent}%</span>}
                </div>
            )}
            <div role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} className={TRACK_CLASSES}>
                <div className={cn(FILL_CLASSES, PROGRESS_ACCENT_BG_CLASSES[accent])} style={{ width: `${percent}%` }} />
            </div>
        </div>
    );
});

export default ProgressBar;
